import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle, XCircle, CreditCard, LayoutDashboard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { payFranchiseOrder, track } from "@/lib/franchisePay";
import { Button } from "@/components/ui/button";
import { ApplicationForm } from "./ApplicationForm";
import { DocUpload } from "./DocUpload";
import { fmt } from "./sections";
import { toast } from "sonner";

const STATUS = {
  payment_pending: [Clock, "Payment pending", "Pay the franchise fee to send your application for review.", "text-yellow-700 bg-yellow-50 border-yellow-200"],
  under_review: [Clock, "Under review", "Your payment is confirmed. Our team is reviewing your application.", "text-blue-700 bg-blue-50 border-blue-200"],
  documents_required: [AlertTriangle, "Documents required", "Please upload the documents requested below.", "text-orange-700 bg-orange-50 border-orange-200"],
  approved: [CheckCircle2, "Approved", "Your institute partner dashboard is active.", "text-emerald-700 bg-emerald-50 border-emerald-200"],
  rejected: [XCircle, "Not approved", "Your application was not approved.", "text-crimson bg-crimson/5 border-crimson/20"],
};

function FeeBox({ fees }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="fr-fee-breakdown">
      <h3 className="font-heading font-bold text-navy mb-4">Fee breakdown</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span>Franchise fee</span><span>{fmt(fees.fee)}</span></div>
        <div className="flex justify-between"><span>GST ({fees.gst_percent}%)</span><span data-testid="fr-gst">{fmt(fees.gst)}</span></div>
        <div className="flex justify-between border-t border-border pt-3 font-heading font-black text-lg text-navy"><span>Total payable</span><span data-testid="fr-total">{fmt(fees.total)}</span></div>
      </div>
    </div>
  );
}

function StatusView({ a, fees, reload, user }) {
  const [paying, setPaying] = useState(false);
  const [docs, setDocs] = useState([]);
  const [Icon, label, hint, cls] = STATUS[a.status] || STATUS.under_review;
  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/franchise/applications/${a.id}/pay`);
      const st = await payFranchiseOrder(data, { description: `Franchise fee ${a.ref_no}`, prefill: { name: a.rep_name, email: a.email, contact: a.mobile } });
      if (st === "paid") { track("checkout_success", { ref: a.ref_no }); toast.success("Payment confirmed. Your application is under review."); }
      if (st === "processing") toast.info("Payment received — confirmation from Razorpay is pending. This page will update shortly.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setPaying(false);
  };
  const sendDocs = async () => {
    try { await api.put(`/franchise/applications/${a.id}`, { new_documents: docs }); setDocs([]); toast.success("Documents sent for review"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="fr-status-view">
      <div className="space-y-6">
        <div className={`border rounded-2xl p-6 ${cls}`} data-testid={`fr-status-${a.status}`}>
          <div className="flex items-center gap-2 font-heading text-xl font-bold"><Icon size={22} /> {label}</div>
          <p className="text-sm mt-1">{hint}</p>
          {a.remark && <p className="text-sm mt-3 font-medium">Note from VIRIKS: {a.remark}</p>}
          {a.status === "approved" && (
            <Button className="mt-4 viriks-navy text-white" onClick={() => { window.location.href = user.is_institute ? "/institute" : "/login"; }} data-testid="fr-open-dashboard"><LayoutDashboard size={15} className="mr-1" /> {user.is_institute ? "Open partner dashboard" : "Log in again to open dashboard"}</Button>
          )}
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="text-sm text-muted-foreground">Application reference</div>
          <div className="font-mono text-2xl font-bold text-navy" data-testid="fr-ref-no">{a.ref_no}</div>
          <div className="text-sm mt-2">{a.institute_name} • {a.district}, {a.state}</div>
          <ol className="mt-6 border-l border-border pl-5 space-y-4">
            {a.history.map((h, i) => (
              <li key={i} className="relative text-sm"><span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full bg-amber" />
                <div className="font-semibold capitalize">{h.status.replace(/_/g, " ")}</div>
                <div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString("en-IN")}{h.remark ? ` — ${h.remark}` : ""}</div>
              </li>
            ))}
          </ol>
        </div>
        {a.status === "documents_required" && (
          <div className="bg-white border border-border rounded-2xl p-6 space-y-4">
            <h3 className="font-heading font-bold text-navy">Upload additional documents</h3>
            <DocUpload docs={docs} setDocs={setDocs} testid="fr-more-docs" />
            <Button disabled={!docs.length} onClick={sendDocs} className="viriks-navy text-white" data-testid="fr-send-docs">Send for review</Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        <FeeBox fees={a.payment_status === "paid" ? a.fees : fees} />
        {a.payment_status === "paid"
          ? <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="fr-paid">Paid {fmt(a.payment?.amount)} on {new Date(a.payment?.paid_at).toLocaleDateString("en-IN")}<br /><span className="font-mono text-xs">{a.payment?.razorpay_payment_id}</span></div>
          : a.status === "payment_pending" && <Button onClick={pay} disabled={paying} className="w-full h-12 rounded-full viriks-amber text-white font-semibold" data-testid="fr-pay"><CreditCard size={16} className="mr-2" /> {paying ? "Processing..." : `Pay ${fmt(fees.total)}`}</Button>}
        <p className="text-xs text-muted-foreground">Payments are processed securely by Razorpay and confirmed on our server before your application moves to review.</p>
      </div>
    </div>
  );
}

export default function FranchiseApply() {
  const { user, loading } = useAuth();
  const [state, setState] = useState(null);
  const load = useCallback(() => api.get("/franchise/my-application").then(({ data }) => setState(data)), []);
  const afterSubmit = () => { window.scrollTo({ top: 0, behavior: "smooth" }); load(); };
  useEffect(() => { if (user) load(); }, [user, load]);

  let body;
  if (loading || (user && !state)) body = <p className="text-muted-foreground">Loading...</p>;
  else if (!user) body = (
    <div className="bg-white border border-border rounded-2xl p-8 max-w-lg" data-testid="fr-login-required">
      <h2 className="font-heading text-xl font-bold text-navy">Sign in to apply</h2>
      <p className="text-sm text-muted-foreground mt-2">Your application, payment and partner dashboard are linked to your VIRIKS account.</p>
      <div className="flex flex-wrap gap-3 mt-6">
        <Link to="/register?next=/institute-franchise/apply"><Button className="viriks-amber text-white" data-testid="fr-register">Create account</Button></Link>
        <Link to="/login" state={{ from: { pathname: "/institute-franchise/apply" } }}><Button variant="outline" data-testid="fr-login">Log in</Button></Link>
      </div>
    </div>
  );
  else if (["super_admin", "admin", "sub_admin"].includes(user.role)) body = <p data-testid="fr-admin-note">Admins review applications in <Link to="/admin/franchise" className="underline">Admin → Franchise</Link>.</p>;
  else if (state.application && state.application.status !== "rejected") body = <StatusView a={state.application} fees={state.fees} reload={load} user={user} />;
  else body = (
    <>
      {state.application && <div className="mb-6 text-sm border border-crimson/20 bg-crimson/5 text-crimson rounded-xl p-4">Previous application {state.application.ref_no} was not approved{state.application.remark ? `: ${state.application.remark}` : "."} You can submit a new application.</div>}
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <ApplicationForm user={user} onSubmitted={afterSubmit} />
        <div className="lg:sticky lg:top-24"><FeeBox fees={state.fees} /></div>
      </div>
    </>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 min-h-[70vh]" data-testid="franchise-apply-page">
      <Link to="/institute-franchise" className="text-sm text-muted-foreground hover:text-navy">← Institute Franchise</Link>
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy mt-3 mb-8">Franchise Application</h1>
      {body}
    </div>
  );
}
